import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Package,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  TrendingUp,
  Calendar,
  MapPin,
  User,
  Phone,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Plus,
  TrendingDown,
  X,
  ShoppingCart,
  Printer,
  FileText,
  Download,
  PackageCheck,
  Trash,
  Crown,
  StickyNote,
  Paperclip,
} from "lucide-react";
import {
  getActiveShippingTiers,
  ShippingTier,
} from "../services/shippingService";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Pagination } from "../components/ui/pagination";
import { formatCurrency, formatDate } from "../lib/utils";
import { useOrdersWithProcurement } from "../hooks/useOrdersWithProcurement";
import { useOrderStatistics } from "../hooks/useOrderStatistics";
import { useDiscounts } from "../hooks/useDiscounts";
import { OrderStatus, Order } from "../types";
import { OrderDetailsModal } from "../components/OrderDetailsModal";
import { UpdateStatusModal } from "../components/UpdateStatusModal";
import { AddTrackingModal } from "../components/AddTrackingModal";
import { PackOrderModal } from "../components/PackOrderModal";
import { ProcurementBatchesModal } from "../components/ProcurementBatchesModal";
import { CreateBatchModal } from "../components/CreateBatchModal";
import { EditOrderModal } from "../components/EditOrderModal";
import { DhlExportModal } from "../components/DhlExportModal";
import { CreateOrderModal } from "../components/CreateOrderModal";
import { InvoicePreviewModal } from "../components/InvoicePreviewModal";
import { AddOrderNoteModal } from "../components/AddOrderNoteModal";
import { OrderDocumentsModal } from "../components/OrderDocumentsModal";
import OrderSort from "../components/OrderSort";
import {
  updateOrderStatus,
  updateOrderTracking,
  refundOrder,
  packOrder,
  deleteOrder,
  updateOrderNotes,
} from "../services/orderService";
import { fetchCarriers, Carrier } from "../services/carrierService";
import { generateShippingLabel } from "../services/easypostService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";

// Currency conversion helpers using order's stored metadata
const getOrderCurrency = (order: Order) => {
  return order?.purchaseCurrency || 'USD';
};

const getOrderExchangeRates = (order: Order) => {
  return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
};

// Convert USD price to order's display currency using stored exchange rate
const getConvertedItemPriceFromOrder = (usdPrice: number, order: Order) => {
  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);
  
  if (currency === 'USD') {
    return usdPrice;
  }
  
  const rate = rates[currency] || 1;
  return Math.ceil(usdPrice * rate);
};

// Format currency with order's currency symbol and code
const formatOrderCurrency = (amount: number, order: Order) => {
  const currency = getOrderCurrency(order);
  
  if (currency === 'USD') {
    return `$${amount.toFixed(2)} USD`;
  } else if (currency === 'CAD') {
    return `$${amount.toFixed(2)} CAD`;
  } else if (currency === 'GBP') {
    return `£${amount.toFixed(2)} GBP`;
  }
  
  return `$${amount.toFixed(2)}`;
};

const getCustomDutiesFromOrder = (order: Order) => {
  const duties = (order as any)?.customDuties;
  if (!duties || !duties.amountUsd) return 0;

  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);

  if (
    duties.currency &&
    duties.currency === currency &&
    typeof duties.amountConverted === 'number'
  ) {
    return duties.amountConverted;
  }

  const usdAmount = duties.amountUsd;
  if (currency === 'USD') return usdAmount;
  const rate = rates[currency as keyof typeof rates] || 1;
  return Math.ceil(usdAmount * rate);
};

// Helper function to get effective price (sale price if on sale, otherwise regular price)
const getEffectivePrice = (item: any) => {
  if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
    return item.onSalePrice;
  }
  return item.price;
};

// Calculate order total using per-item conversion for accuracy
const getOrderTotal = (order: Order) => {
  if (!order.items) return 0;
  
  // Calculate converted subtotal (per-item conversion)
  const convertedSubtotal = order.items.reduce((total, item) => {
    const unitPriceUsd = getEffectivePrice(item);
    const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, order);
    return total + (convertedUnitPrice * item.quantity);
  }, 0);
  
  // Calculate converted shipping (individual conversion)
  const baseShippingCost = order?.shipping?.cost || 0;
  const convertedShipping = order?.discount?.type === 'free_shipping' 
    ? 0 
    : getConvertedItemPriceFromOrder(baseShippingCost, order);
  
  // Calculate converted priority fee (individual conversion)
  const convertedPriorityFee = getConvertedItemPriceFromOrder(order?.shipping?.priorityFee || 0, order);
  
  // Calculate converted discount (individual conversion)
  const convertedDiscount = order?.discount?.type !== 'free_shipping' 
    ? getConvertedItemPriceFromOrder(order?.discount?.amount || 0, order) 
    : 0;

  const convertedDuties = getCustomDutiesFromOrder(order);
  
  // Total calculation: subtotal + shipping + priority - discount + duties
  return convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount + convertedDuties;
};

// Calculate order subtotal using per-item conversion
const getOrderSubtotal = (order: Order) => {
  if (!order.items) return 0;
  
  return order.items.reduce((total, item) => {
    const unitPriceUsd = getEffectivePrice(item);
    const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, order);
    return total + (convertedUnitPrice * item.quantity);
  }, 0);
};

export function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showAddTrackingModal, setShowAddTrackingModal] = useState(false);
  const [showPackOrderModal, setShowPackOrderModal] = useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [showRefundOrderModal, setShowRefundOrderModal] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showProcurementBatchesModal, setShowProcurementBatchesModal] = useState(false);
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false);
  const [showDhlExportModal, setShowDhlExportModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showOrderDocumentsModal, setShowOrderDocumentsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [orderToRefund, setOrderToRefund] = useState<Order | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [carriersLoading, setCarriersLoading] = useState(true);
  const [shippingTiers, setShippingTiers] = useState<ShippingTier[]>([]);
  const [shippingTiersLoading, setShippingTiersLoading] = useState(true);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatuses,
    selectedPaymentStatuses,
    selectedShippingMethods,
    selectedDiscountCodes,
    selectedPackedStatuses,
    priorityProcessingFilter,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    setSelectedStatuses,
    setSelectedPaymentStatuses,
    setSelectedShippingMethods,
    setSelectedDiscountCodes,
    setSelectedPackedStatuses,
    setPriorityProcessingFilter,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
    updateOrderInCache,
    removeOrderFromCache,
    invalidateCache,
  } = useOrdersWithProcurement({ pageSize: 20 });

  const {
    statistics,
    percentageChanges,
    previousSnapshot,
    loading: statisticsLoading,
    error: statisticsError,
    refresh: refreshStatistics,
  } = useOrderStatistics();

  const {
    discountCodes,
    loading: discountCodesLoading,
    error: discountCodesError,
  } = useDiscounts();

  // Handle URL search parameters from notifications
  useEffect(() => {
    const searchParam = searchParams.get("search");
    if (searchParam && searchParam !== searchQuery) {
      // Auto-trigger search with the parameter value
      handleSearch(searchParam);

      // Optionally clear the URL parameter after triggering search to keep URL clean
      // but preserve the search functionality
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("search");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, searchQuery, handleSearch, setSearchParams]);

  // Load carriers on component mount
  useEffect(() => {
    const loadCarriers = async () => {
      try {
        setCarriersLoading(true);
        const carrierData = await fetchCarriers();
        setCarriers(carrierData);
      } catch (error) {
        console.error("Failed to load carriers:", error);
        // Continue with empty carriers array - fallback will handle this
      } finally {
        setCarriersLoading(false);
      }
    };

    loadCarriers();
  }, []);

  // Load shipping tiers on component mount
  useEffect(() => {
    const loadShippingTiers = async () => {
      try {
        setShippingTiersLoading(true);
        const tiersData = await getActiveShippingTiers();
        setShippingTiers(tiersData);
      } catch (error) {
        console.error("Failed to load shipping tiers:", error);
        toast.error("Failed to load shipping options");
      } finally {
        setShippingTiersLoading(false);
      }
    };

    loadShippingTiers();
  }, []);

  // Show error toasts when errors occur
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Show error toasts for order statistics errors
  useEffect(() => {
    if (statisticsError) {
      toast.error("Failed to load order statistics");
    }
  }, [statisticsError]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
          icon: Clock,
          label: "Pending",
        };
      case "paid":
        return {
          color: "bg-blue-100 text-blue-800",
          hoverColor: "hover:bg-blue-100 hover:text-blue-800",
          icon: Package,
          label: "Paid",
        };
      case "processed":
        return {
          color: "bg-blue-100 text-blue-900",
          hoverColor: "hover:bg-blue-100 hover:text-blue-900",
          icon: Package,
          label: "Processed",
        };
      case "shipped":
        return {
          color: "bg-purple-100 text-purple-800",
          hoverColor: "hover:bg-purple-100 hover:text-purple-800",
          icon: Truck,
          label: "Shipped",
        };
      case "delivered":
        return {
          color: "bg-green-100 text-green-800",
          hoverColor: "hover:bg-green-100 hover:text-green-800",
          icon: CheckCircle,
          label: "Delivered",
        };
      case "cancelled":
        return {
          color: "bg-red-100 text-red-800",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
          icon: AlertCircle,
          label: "Cancelled",
        };
      case "payment_failed":
        return {
          color: "bg-red-100 text-red-800",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
          icon: AlertCircle,
          label: "Payment Failed",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          hoverColor: "hover:bg-gray-100 hover:text-gray-800",
          icon: Clock,
          label: "Unknown",
        };
    }
  };

  // Define order statuses for filtering (matching badge colors)
  const orderStatusFilters = [
    {
      key: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
      hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
    },
    {
      key: "paid",
      label: "Paid",
      color: "bg-blue-100 text-blue-800",
      hoverColor: "hover:bg-blue-100 hover:text-blue-800",
    },
    {
      key: "processed",
      label: "Processed",
      color: "bg-blue-100 text-blue-900",
      hoverColor: "hover:bg-blue-100 hover:text-blue-900",
    },
    {
      key: "shipped",
      label: "Shipped",
      color: "bg-purple-100 text-purple-800",
      hoverColor: "hover:bg-purple-100 hover:text-purple-800",
    },
    {
      key: "delivered",
      label: "Delivered",
      color: "bg-green-100 text-green-800",
      hoverColor: "hover:bg-green-100 hover:text-green-800",
    },
    {
      key: "cancelled",
      label: "Cancelled",
      color: "bg-red-100 text-red-800",
      hoverColor: "hover:bg-red-100 hover:text-red-800",
    },
  ];

  // Define payment status filters
  const paymentStatusFilters = [
    {
      key: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
      hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
    },
    {
      key: "succeeded",
      label: "Succeeded",
      color: "bg-green-100 text-green-800",
      hoverColor: "hover:bg-green-100 hover:text-green-800",
    },
    {
      key: "failed",
      label: "Failed",
      color: "bg-red-100 text-red-800",
      hoverColor: "hover:bg-red-100 hover:text-red-800",
    },
    {
      key: "refund_pending",
      label: "Refund Pending",
      color: "bg-orange-100 text-orange-800",
      hoverColor: "hover:bg-orange-100 hover:text-orange-800",
    },
    {
      key: "refunded",
      label: "Refunded",
      color: "bg-purple-100 text-purple-800",
      hoverColor: "hover:bg-purple-100 hover:text-purple-800",
    },
  ];

  // Helper functions to extract data that might not be in the Order type yet
  const getOrderNumber = (order: any) => {
    return order.orderNumber || order.id;
  };

  const getPaymentStatus = (order: any) => {
    return order.payment?.status || "pending";
  };

  const getTrackingNumber = (order: any) => {
    return order.tracking?.trackingNumber || null;
  };

  const getCustomerAddress = (order: any) => {
    // Check for the correct address structure as used in customer-facing app
    if (order.customer?.address?.line1) {
      const addr = order.customer.address;
      return `${addr.line1}, ${addr.city}, ${addr.state} ${addr.postal_code}`;
    }

    // Fallback: Check for addresses array (legacy structure)
    if (order.customer?.addresses && order.customer.addresses.length > 0) {
      const addr = order.customer.addresses[0];
      return `${addr.address1 || addr.line1}, ${addr.city}, ${addr.state} ${
        addr.zipCode || addr.postal_code
      }`;
    }

    return "No address provided";
  };

  const getEstimatedDelivery = (order: any) => {
    return order.tracking?.estimatedDelivery || null;
  };

  const getProductName = (item: any) => {
    // Try different possible field names for product name
    return item.name || item.productName || item.title || "Unknown Product";
  };

  const getPaymentStatusConfig = (status: string) => {
    switch (status) {
      case "succeeded":
        return {
          color: "bg-green-100 text-green-800",
          hoverColor: "hover:bg-green-100 hover:text-green-800",
          label: "Succeeded",
        };
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
          label: "Pending",
        };
      case "failed":
        return {
          color: "bg-red-100 text-red-800",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
          label: "Failed",
        };
      case "refund_pending":
        return {
          color: "bg-orange-100 text-orange-800",
          hoverColor: "hover:bg-orange-100 hover:text-orange-800",
          label: "Refund Pending",
        };
      case "refunded":
        return {
          color: "bg-purple-100 text-purple-800",
          hoverColor: "hover:bg-purple-100 hover:text-purple-800",
          label: "Refunded",
        };
      // Legacy support for any old payment statuses that might exist
      case "paid":
      case "completed":
        return {
          color: "bg-green-100 text-green-800",
          hoverColor: "hover:bg-green-100 hover:text-green-800",
          label: "Succeeded",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          hoverColor: "hover:bg-gray-100 hover:text-gray-800",
          label: "Unknown",
        };
    }
  };

  // Extract individual statistics values
  const paidSucceededOrders = statistics?.paidSucceededOrders ?? 0;
  const aggregateShippedOrders = statistics?.shippedOrders ?? 0;
  const aggregateTotalOrders = statistics?.totalOrders ?? 0;
  const aggregateTotalRevenue = statistics?.totalRevenue ?? 0;

  // Calculate stats (using aggregate data instead of current page data)
  const totalOrdersCount = aggregateTotalOrders;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const shippedOrders = orders.filter((o) => o.status === "shipped").length;
  const totalRevenue = aggregateTotalRevenue;

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle refresh for both orders and statistics
  const handleRefresh = async () => {
    try {
      await Promise.all([refresh(), refreshStatistics()]);
      toast.success("Orders refreshed successfully");
    } catch (error) {
      console.error("Error refreshing orders:", error);
      toast.error("Failed to refresh orders. Please try again.");
    }
  };

  // Helper function to refresh orders after updates while maintaining current mode
  const refreshOrdersAfterUpdate = async () => {
    if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
      // Invalidate cache and re-trigger current search/filter/sort to ensure updated orders are displayed correctly
      invalidateCache();
      
      // Re-trigger search if active
      if (searchQuery) {
        handleSearch(searchQuery);
      }
      
      // Re-trigger filters if active
      if (isFilterMode || isCombinedMode) {
        // Re-set the current filters to trigger the filter logic
        setSelectedStatuses([...selectedStatuses]);
        setSelectedPaymentStatuses([...selectedPaymentStatuses]);
        setSelectedShippingMethods([...selectedShippingMethods]);
        setSelectedDiscountCodes([...selectedDiscountCodes]);
        setPriorityProcessingFilter(priorityProcessingFilter);
      }
      
      // Re-trigger sorting if active
      if (isSortMode || isCombinedMode) {
        setSorting(sortField, sortOrder);
      }
      
      await refreshStatistics();
    } else {
      // For browse mode, do a full refresh (which includes statistics)
      await Promise.all([refresh(), refreshStatistics()]);
    }
  };

  // Handle view details
  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  // Handle copy order number
  const handleCopyOrderNumber = async (order: Order) => {
    const orderNumber = order.id;
    try {
      await navigator.clipboard.writeText(orderNumber);
      toast.success(`Order ID ${orderNumber} copied to clipboard`);
    } catch (error) {
      console.error("Failed to copy order number:", error);
      toast.error("Failed to copy order number");
    }
  };

  // Handle update status
  const handleUpdateStatus = (order: Order) => {
    setSelectedOrder(order);
    setShowUpdateStatusModal(true);
  };

  // Handle add tracking
  const handleAddTracking = (order: Order) => {
    setSelectedOrder(order);
    setShowAddTrackingModal(true);
  };

  // Handle add note
  const handleAddOrderNote = (order: Order) => {
    setSelectedOrder(order);
    setShowAddNoteModal(true);
  };

  // Handle manage order documents
  const handleManageDocuments = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDocumentsModal(true);
  };

  // Handle view/print EasyPost label
  const handleViewEasyPostLabel = (order: Order) => {
    if (order.easyPostLabel?.labelUrl) {
      // Open label in new tab for viewing/printing
      window.open(order.easyPostLabel.labelUrl, '_blank');
    } else if (order.easyPostLabel?.postageLabel?.label_url) {
      // Fallback to postage label URL
      window.open(order.easyPostLabel.postageLabel.label_url, '_blank');
    } else {
      toast.error('No EasyPost label found for this order');
    }
  };

  // Handle edit order
  const handleEditOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowEditOrderModal(true);
  };

  // Handle DHL export
  const handleDhlExport = (order: Order) => {
    setSelectedOrder(order);
    setShowDhlExportModal(true);
  };

  // Handle order update from edit modal
  const handleOrderUpdated = async (updatedOrder: Order) => {
    setShowEditOrderModal(false);
    await refreshOrdersAfterUpdate();
    toast.success('Order updated successfully');
  };

  // Handle pack order
  const handlePackOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowPackOrderModal(true);
  };

  // Handle status update submission
  const handleStatusUpdate = async (
    orderId: string,
    newStatus: OrderStatus,
    notes?: string
  ) => {
    try {
      await updateOrderStatus(orderId, newStatus, notes);
      await refreshOrdersAfterUpdate();
    } catch (error) {
      console.error("Failed to update order status:", error);
      toast.error("Failed to update order status. Please try again.");
      throw error;
    }
  };

  // Handle tracking update submission
  const handleTrackingUpdate = async (orderId: string, trackingData: any) => {
    try {
      await updateOrderTracking(orderId, trackingData);
      await refreshOrdersAfterUpdate();
    } catch (error) {
      console.error("Failed to update tracking information:", error);
      toast.error("Failed to update tracking information. Please try again.");
      throw error;
    }
  };

  // Handle note submission
  const handleSaveOrderNote = async (orderId: string, note: string) => {
    try {
      await updateOrderNotes(orderId, note);
      await refreshOrdersAfterUpdate();
    } catch (error) {
      console.error("Failed to update order note:", error);
      throw error;
    }
  };

  // Handle EasyPost label generation
  const handleEasyPostLabelGeneration = async (orderId: string) => {
    try {
      // For now, just show a message that it's coming soon
      toast.info("EasyPost label generation is coming soon! Please configure your shipping settings first.");
      // Future implementation will call the backend service:
      // await generateShippingLabel(orderId, shipFromAddressId, packageDimensionsId);
      // await refreshOrdersAfterUpdate();
    } catch (error) {
      console.error("Failed to generate EasyPost label:", error);
      toast.error("Failed to generate EasyPost label. Please try again.");
      throw error;
    }
  };

  // Handle pack order submission
  const handlePackOrderSubmission = async (orderId: string) => {
    try {
      await packOrder(orderId);
      await refreshOrdersAfterUpdate();

      setShowPackOrderModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error packing order:', error);
    }
  };

  // Handle cancel order
  const handleCancelOrder = (order: Order) => {
    setOrderToCancel(order);
    setShowCancelOrderModal(true);
  };

  // Handle cancel order confirmation
  const handleConfirmCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      setIsCancelling(true);
      await updateOrderStatus(
        orderToCancel.id,
        "cancelled",
        "Order cancelled from admin portal"
      );

      await refreshOrdersAfterUpdate();

      toast.success(
        `Order ${orderToCancel.id} has been cancelled successfully`
      );

      // Close modal and reset state
      setShowCancelOrderModal(false);
      setOrderToCancel(null);
    } catch (error) {
      console.error("Failed to cancel order:", error);
      toast.error("Failed to cancel order. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle refund order
  const handleRefundOrder = (order: Order) => {
    setOrderToRefund(order);
    setRefundReason("");
    setShowRefundOrderModal(true);
  };

  // Handle refund order confirmation
  const handleConfirmRefundOrder = async () => {
    if (!orderToRefund) return;

    try {
      setIsRefunding(true);
      await refundOrder(orderToRefund.id, refundReason || "Order refund requested by admin");
      await refreshOrdersAfterUpdate();

      toast.success(
        `Refund processed successfully for Order ${orderToRefund.id}.`
      );

      // Close modal and reset state
      setShowRefundOrderModal(false);
      setOrderToRefund(null);
      setRefundReason("");
    } catch (error) {
      console.error("Failed to process refund:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to process refund. Please try again."
      );
    } finally {
      setIsRefunding(false);
    }
  };

  const handleDeleteOrder = (order: Order) => {
    setOrderToDelete(order);
    setShowDeleteDialog(true);
  };

  const handleConfirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteOrder(orderToDelete.id);
      removeOrderFromCache(orderToDelete.id);
      await refreshOrdersAfterUpdate();
      toast.success(`Order ${orderToDelete.id} deleted successfully`);
      setShowDeleteDialog(false);
      setOrderToDelete(null);
    } catch (error) {
      console.error("Failed to delete order:", error);
      toast.error("Failed to delete order. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setOrderToDelete(null);
  };

  // Helper function to check if order can be refunded
  const canRefundOrder = (order: Order) => {
    const isCancelled = order.status === "cancelled";
    const paymentStatus = getPaymentStatus(order);
    const isSucceeded = paymentStatus === "succeeded";
    const hasNoRefund = !order.refund;

    return isCancelled && isSucceeded && hasNoRefund;
  };

  const canPackOrder = (order: Order) => {
    const isPaid = order.status === "paid";
    const paymentStatus = getPaymentStatus(order);
    const isSucceeded = paymentStatus === "succeeded";
    const isNotPacked = !order.packed;

    return isPaid && isSucceeded && isNotPacked;
  };

  const handleGenerateInvoice = (order: Order) => {
    setSelectedOrder(order);
    setShowInvoicePreviewModal(true);
  };

  const formatPercentageChange = (percentage: number) => {
    const isPositive = percentage >= 0;
    const formattedPercentage = Math.abs(percentage).toFixed(1);
    return {
      text: `${isPositive ? "+" : "-"}${formattedPercentage}% from last month`,
      className: isPositive ? "text-green-600" : "text-red-600",
      iconClassName: isPositive ? "text-green-500" : "text-red-500",
      Icon: isPositive ? TrendingUp : TrendingDown,
    };
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-between items-center"
      >
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
        </div>
        <Button
          onClick={() => setShowCreateOrderModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Order
        </Button>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Orders
                </p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {totalOrdersCount}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-jamaica-green" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : percentageChanges &&
                previousSnapshot &&
                percentageChanges.totalOrders !== 0 ? (
                <>
                  {(() => {
                    const trend = formatPercentageChange(
                      percentageChanges.totalOrders
                    );
                    return (
                      <>
                        <trend.Icon
                          className={`h-4 w-4 ${trend.iconClassName} mr-1`}
                        />
                        <span className={trend.className}>{trend.text}</span>
                      </>
                    );
                  })()}
                </>
              ) : (
                <span className="text-gray-500">No historical data</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Orders
                </p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-yellow-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-yellow-600">
                    {paidSucceededOrders}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : (
                <span className="text-gray-600">Requires processing</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Shipped Orders
                </p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-purple-600">
                    {aggregateShippedOrders}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : (
                <span className="text-gray-600">In transit</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-yellow" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(totalRevenue)}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-jamaica-yellow" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : percentageChanges &&
                previousSnapshot &&
                percentageChanges.totalRevenue !== 0 ? (
                <>
                  {(() => {
                    const trend = formatPercentageChange(
                      percentageChanges.totalRevenue
                    );
                    return (
                      <>
                        <trend.Icon
                          className={`h-4 w-4 ${trend.iconClassName} mr-1`}
                        />
                        <span className={trend.className}>{trend.text}</span>
                      </>
                    );
                  })()}
                </>
              ) : (
                <span className="text-gray-500">No historical data</span>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search orders by number, customer name, address, or tracking number..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
              />
              {(searchLoading || filterLoading) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <OrderSort
              sortField={sortField}
              sortOrder={sortOrder}
              onSortChange={setSorting}
            />

            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedStatuses.length > 0 ||
                selectedPaymentStatuses.length > 0 ||
                selectedShippingMethods.length > 0 ||
                selectedDiscountCodes.length > 0 ||
                selectedPackedStatuses.length > 0 ||
                priorityProcessingFilter) && (
                <Badge
                  className={`ml-1 border-transparent ${
                    showFilters
                      ? "bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green"
                      : "bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white"
                  }`}
                >
                  {selectedStatuses.length +
                    selectedPaymentStatuses.length +
                    selectedShippingMethods.length +
                    selectedDiscountCodes.length +
                    selectedPackedStatuses.length +
                    (priorityProcessingFilter ? 1 : 0)}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Status Filter */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Status:
              </span>
              {orderStatusFilters.map((status) => (
                <Button
                  key={status.key}
                  variant={
                    selectedStatuses.includes(status.key as OrderStatus)
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    if (selectedStatuses.includes(status.key as OrderStatus)) {
                      setSelectedStatuses(
                        selectedStatuses.filter((s) => s !== status.key)
                      );
                    } else {
                      setSelectedStatuses([
                        ...selectedStatuses,
                        status.key as OrderStatus,
                      ]);
                    }
                  }}
                  className={
                    selectedStatuses.includes(status.key as OrderStatus)
                      ? `${status.color} ${status.hoverColor}`
                      : ""
                  }
                >
                  {status.label}
                </Button>
              ))}

              {/* Priority Processing Filter - Inline with status filters */}
              <Button
                variant={priorityProcessingFilter ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  setPriorityProcessingFilter(!priorityProcessingFilter)
                }
                className={
                  priorityProcessingFilter
                    ? "bg-jamaica-green/10 text-jamaica-green border-jamaica-green/20 hover:bg-jamaica-green/10 hover:text-jamaica-green"
                    : ""
                }
              >
                ⚡ Priority
              </Button>
            </div>

            {/* Payment Status Filters */}
            <div className="flex flex-wrap gap-2 items-center mt-4">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Payment:
              </span>
              {paymentStatusFilters.map((status) => (
                <Button
                  key={status.key}
                  variant={
                    selectedPaymentStatuses.includes(status.key)
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    if (selectedPaymentStatuses.includes(status.key)) {
                      setSelectedPaymentStatuses(
                        selectedPaymentStatuses.filter((s) => s !== status.key)
                      );
                    } else {
                      setSelectedPaymentStatuses([
                        ...selectedPaymentStatuses,
                        status.key,
                      ]);
                    }
                  }}
                  className={
                    selectedPaymentStatuses.includes(status.key)
                      ? `${status.color} ${status.hoverColor}`
                      : ""
                  }
                >
                  {status.label}
                </Button>
              ))}
            </div>

            {/* Packed Status Filters */}
            <div className="flex flex-wrap gap-2 items-center mt-4">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Packed Status:
              </span>
              {[
                { key: 'packed', label: 'Packed', color: 'bg-emerald-100 text-emerald-800', hoverColor: 'hover:bg-emerald-100 hover:text-emerald-800' },
                { key: 'unpacked', label: 'Unpacked', color: 'bg-orange-100 text-orange-800', hoverColor: 'hover:bg-orange-100 hover:text-orange-800' }
              ].map((status) => (
                <Button
                  key={status.key}
                  variant={
                    selectedPackedStatuses.includes(status.key)
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    if (selectedPackedStatuses.includes(status.key)) {
                      setSelectedPackedStatuses(
                        selectedPackedStatuses.filter((s) => s !== status.key)
                      );
                    } else {
                      setSelectedPackedStatuses([
                        ...selectedPackedStatuses,
                        status.key,
                      ]);
                    }
                  }}
                  className={
                    selectedPackedStatuses.includes(status.key)
                      ? `${status.color} ${status.hoverColor}`
                      : ""
                  }
                >
                  {status.label}
                </Button>
              ))}
            </div>

            {/* Shipping Method Filters */}
            <div className="flex flex-wrap gap-2 items-center mt-4">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Shipping:
              </span>
              {shippingTiersLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                  <span className="text-sm text-gray-500">
                    Loading shipping options...
                  </span>
                </div>
              ) : shippingTiers.length === 0 ? (
                <span className="text-sm text-gray-500">
                  No shipping options available
                </span>
              ) : (
                shippingTiers.map((tier) => (
                  <Button
                    key={tier.id}
                    variant={
                      selectedShippingMethods.includes(tier.id)
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    onClick={() => {
                      if (selectedShippingMethods.includes(tier.id)) {
                        setSelectedShippingMethods(
                          selectedShippingMethods.filter((s) => s !== tier.id)
                        );
                      } else {
                        setSelectedShippingMethods([
                          ...selectedShippingMethods,
                          tier.id,
                        ]);
                      }
                    }}
                    className={
                      selectedShippingMethods.includes(tier.id)
                        ? "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                        : ""
                    }
                  >
                    <Truck className="w-3 h-3 mr-1" />
                    {tier.name}
                  </Button>
                ))
              )}
            </div>

            {/* Discount Code Filters */}
            <div className="flex flex-wrap gap-2 items-center mt-4">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Discount Codes:
              </span>
              {discountCodesLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                  <span className="text-sm text-gray-500">
                    Loading discount codes...
                  </span>
                </div>
              ) : discountCodes.length === 0 ? (
                <span className="text-sm text-gray-500">
                  No discount codes available
                </span>
              ) : (
                discountCodes.map((discount) => (
                  <Button
                    key={discount.id}
                    variant={
                      selectedDiscountCodes.includes(discount.code)
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    onClick={() => {
                      if (selectedDiscountCodes.includes(discount.code)) {
                        setSelectedDiscountCodes(
                          selectedDiscountCodes.filter((code) => code !== discount.code)
                        );
                      } else {
                        setSelectedDiscountCodes([
                          ...selectedDiscountCodes,
                          discount.code,
                        ]);
                      }
                    }}
                    className={
                      selectedDiscountCodes.includes(discount.code)
                        ? "bg-green-100 text-green-700 border-green-300 hover:bg-green-100 hover:text-green-700"
                        : ""
                    }
                  >
                    🎫 {discount.code}
                  </Button>
                ))
              )}
            </div>

            {/* Clear Filters */}
            {(selectedStatuses.length > 0 ||
              selectedPaymentStatuses.length > 0 ||
              selectedShippingMethods.length > 0 ||
              selectedDiscountCodes.length > 0 ||
              selectedPackedStatuses.length > 0 ||
              priorityProcessingFilter ||
              sortField) && (
              <div className="pt-2 border-t border-gray-100 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSelectedPaymentStatuses([]);
                    setSelectedShippingMethods([]);
                    setSelectedDiscountCodes([]);
                    setSelectedPackedStatuses([]);
                    setPriorityProcessingFilter(false);
                    setSorting(null, "desc");
                  }}
                  className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Orders Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="admin-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-jamaica-green" />
                Orders ({totalCount})
                {isCombinedMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-black border-jamaica-black hover:bg-transparent hover:text-jamaica-black hover:border-jamaica-black"
                  >
                    Combined Mode
                  </Badge>
                )}
                {isSearchMode && !isCombinedMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-green border-jamaica-green hover:bg-transparent hover:text-jamaica-green hover:border-jamaica-green"
                  >
                    Search Mode
                  </Badge>
                )}
                {isFilterMode && !isCombinedMode && !isSortMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-yellow border-jamaica-yellow hover:bg-transparent hover:text-jamaica-yellow hover:border-jamaica-yellow"
                  >
                    Filter Mode
                  </Badge>
                )}
                {isSortMode && !isSearchMode && !isFilterMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-purple-600 border-purple-600 hover:bg-transparent hover:text-purple-600 hover:border-purple-600"
                  >
                    Sort Mode
                  </Badge>
                )}
              </CardTitle>
              <Button
                onClick={() => setShowProcurementBatchesModal(true)}
                variant="outline"
                className="flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
              >
                <ShoppingCart className="h-4 w-4" />
                Procurement Batches
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Error Loading Orders
                </h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : loading || searchLoading || filterLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <span className="ml-2 text-gray-600">
                  {searchLoading
                    ? "Searching orders..."
                    : filterLoading
                    ? "Filtering orders..."
                    : "Loading orders..."}
                </span>
              </div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Orders Found
                </h3>
                <p className="text-gray-600 mb-4">
                  {isCombinedMode
                    ? "No orders match your search query and filters."
                    : isSearchMode
                    ? "No orders match your search criteria."
                    : isFilterMode
                    ? "No orders match the selected filters."
                    : "No orders have been placed yet."}
                </p>
                {(searchQuery ||
                  isFilterMode ||
                  priorityProcessingFilter ||
                  sortField) && (
                  <div className="space-y-2">
                    {searchQuery && (
                      <Button
                        onClick={() => handleSearch("")}
                        variant="outline"
                      >
                        Clear Search
                      </Button>
                    )}
                    {(isFilterMode ||
                      priorityProcessingFilter ||
                      sortField) && (
                      <Button
                        onClick={() => {
                          setSelectedStatuses([]);
                          setSelectedPaymentStatuses([]);
                          setSelectedShippingMethods([]);
                          setSelectedDiscountCodes([]);
                          setPriorityProcessingFilter(false);
                          setSorting(null, "desc");
                        }}
                        variant="outline"
                      >
                        Clear Filters & Sort
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Order</TableHead>
                        <TableHead className="w-56">Customer</TableHead>
                        <TableHead className="w-32">Items</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="w-36">Total</TableHead>
                        <TableHead className="w-48">Date</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order, index) => {
                        const statusConfig = getStatusConfig(order.status);
                        const paymentConfig = getPaymentStatusConfig(
                          getPaymentStatus(order)
                        );
                        const StatusIcon = statusConfig.icon;
                        const isRushdOrder = !!(
                          order.rushdMembership ||
                          order.customer?.rushdMembership?.isMember
                        );

                        // Ready-to-pack styling
                        const readyToPackStyles = order.isReadyToPack ? {
                          className: "hover:bg-gray-50 transition-all duration-300 border-l-4 border-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent",
                          style: { 
                            boxShadow: order.isReadyToPack ? '0 0 0 1px rgba(16, 185, 129, 0.1)' : 'none' 
                          }
                        } : {
                          className: "hover:bg-gray-50 transition-colors",
                          style: {}
                        };

                        const rowClassName = isRushdOrder
                          ? "hover:bg-amber-50 transition-all duration-300 border-l-4 border-amber-500 bg-gradient-to-r from-amber-50/80 to-white"
                          : readyToPackStyles.className;

                        const rowStyle = isRushdOrder
                          ? { boxShadow: "0 0 0 1px rgba(245, 158, 11, 0.25)" }
                          : readyToPackStyles.style;

                        return (
                          <motion.tr
                            key={order.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className={rowClassName}
                            style={rowStyle}
                          >
                            <TableCell className="font-medium">
                              <div className="space-y-1">
                                <p
                                  className="text-xs text-gray-500 hover:text-jamaica-green cursor-pointer transition-colors break-all"
                                  onClick={() => handleCopyOrderNumber(order)}
                                  title="Click to copy order ID"
                                >
                                  ID:{" "}
                                  {(order.orderNumber || order.id).substring(
                                    0,
                                    12
                                  )}
                                  ...
                                </p>
                                {isRushdOrder && (
                                  <div className="flex items-center gap-1">
                                    <Crown className="w-3 h-3 text-amber-500" />
                                    <p className="text-[10px] text-amber-700 font-semibold">
                                      Rush&apos;d Order
                                    </p>
                                  </div>
                                )}
                                {getTrackingNumber(order) && (
                                  <p className="text-xs text-jamaica-green font-medium">
                                    Tracking: {getTrackingNumber(order)}
                                  </p>
                                )}
                                {order.easyPostLabel && (
                                  <div className="flex items-center gap-1">
                                    <Printer className="w-3 h-3 text-jamaica-green" />
                                    <p className="text-xs text-jamaica-green font-medium">
                                      Label: {order.easyPostLabel.shipment.carrier}
                                    </p>
                                  </div>
                                )}
                                {order.isReadyToPack && (
                                  <div 
                                    className="flex items-center gap-1 cursor-help"
                                    title="All items for this order have been collected from procurement and are ready to be packed"
                                  >
                                    <PackageCheck className="w-3 h-3 text-emerald-600" />
                                    <p className="text-xs text-emerald-600 font-medium">
                                      Ready to Pack
                                    </p>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="w-40">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-gray-400" />
                                  <p className="font-medium text-gray-900 text-sm truncate">
                                    {order.customer.name}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <p
                                    className="text-xs text-gray-500 truncate max-w-32"
                                    title={getCustomerAddress(order)}
                                  >
                                    {getCustomerAddress(order).substring(0, 50)}
                                    ...
                                  </p>
                                </div>
                                {order.shipping?.priorityProcessing && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs bg-jamaica-green/10 text-jamaica-green px-2 py-0.5 rounded-full font-medium">
                                      ⚡ Priority
                                    </span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="w-32">
                              <div className="space-y-1">
                                <p className="font-medium text-gray-900 text-sm">
                                  {order.items.length} items
                                </p>
                                <div className="text-xs text-gray-500 max-w-28">
                                  {order.items.slice(0, 1).map((item, idx) => (
                                    <p
                                      key={idx}
                                      className="truncate"
                                      title={`${
                                        item.quantity
                                      }x ${getProductName(item)}`}
                                    >
                                      {item.quantity}x{" "}
                                      {getProductName(item).substring(0, 12)}...
                                    </p>
                                  ))}
                                  {order.items.length > 1 && (
                                    <p className="text-xs text-gray-400">
                                      +{order.items.length - 1} more
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={`${statusConfig.color} ${statusConfig.hoverColor}`}
                              >
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={`${paymentConfig.color} ${paymentConfig.hoverColor}`}
                              >
                                {paymentConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <p className="font-semibold text-gray-900">
                                  {formatOrderCurrency(getOrderTotal(order), order)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Subtotal: {formatOrderCurrency(getOrderSubtotal(order), order)}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <p className="text-sm text-gray-600">
                                    {formatDate(order.createdAt)}
                                  </p>
                                </div>
                                {getEstimatedDelivery(order) && (
                                  <div className="flex items-center gap-1">
                                    <Truck className="w-3 h-3 text-jamaica-green" />
                                    <p className="text-[10px] text-jamaica-green font-medium">
                                      Est. delivery:{" "}
                                      {formatDate(getEstimatedDelivery(order))}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleViewDetails(order)}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleGenerateInvoice(order)}
                                    className="text-indigo-600"
                                  >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Generate Invoice
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDhlExport(order)}
                                    className="text-blue-600"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Export to CSV
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleUpdateStatus(order)}
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Update Status
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleAddOrderNote(order)}
                                  >
                                    <StickyNote className="w-4 h-4 mr-2" />
                                    Add Note
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleManageDocuments(order)}
                                  >
                                    <Paperclip className="w-4 h-4 mr-2" />
                                    Manage Documents
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleEditOrder(order)}
                                    className="text-purple-600"
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit Order
                                  </DropdownMenuItem>
                                  {canPackOrder(order) && (
                                    <DropdownMenuItem
                                      onClick={() => handlePackOrder(order)}
                                      className="text-emerald-600"
                                    >
                                      <Package className="w-4 h-4 mr-2" />
                                      Pack Order
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleAddTracking(order)}
                                  >
                                    <Truck className="w-4 h-4 mr-2" />
                                    Add Tracking
                                  </DropdownMenuItem>
                                  {order.easyPostLabel && (
                                    <DropdownMenuItem
                                      onClick={() => handleViewEasyPostLabel(order)}
                                      className="text-jamaica-green"
                                    >
                                      <Printer className="w-4 h-4 mr-2" />
                                      View Shipping Label
                                    </DropdownMenuItem>
                                  )}
                                  {canRefundOrder(order) && (
                                    <DropdownMenuItem
                                      className="text-orange-600"
                                      onClick={() => handleRefundOrder(order)}
                                    >
                                      <DollarSign className="w-4 h-4 mr-2" />
                                      Refund Order
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={() => handleCancelOrder(order)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Cancel Order
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-700"
                                    onClick={() => handleDeleteOrder(order)}
                                  >
                                    <Trash className="w-4 h-4 mr-2" />
                                    Delete Order
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                    totalItems={totalCount}
                    itemsPerPage={20}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onPackOrder={handlePackOrder}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        order={selectedOrder}
        isOpen={showUpdateStatusModal}
        onClose={() => setShowUpdateStatusModal(false)}
        onStatusUpdate={handleStatusUpdate}
      />

      <AddOrderNoteModal
        order={selectedOrder}
        isOpen={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
        onSaveNote={handleSaveOrderNote}
      />

      <OrderDocumentsModal
        order={selectedOrder}
        isOpen={showOrderDocumentsModal}
        onClose={() => setShowOrderDocumentsModal(false)}
      />

      {/* Add Tracking Modal */}
      <AddTrackingModal
        order={selectedOrder}
        isOpen={showAddTrackingModal}
        onClose={() => setShowAddTrackingModal(false)}
        onTrackingUpdate={handleTrackingUpdate}
        onEasyPostLabelGeneration={handleEasyPostLabelGeneration}
        carriers={carriers}
        loading={carriersLoading}
      />

      {/* Pack Order Modal */}
      <PackOrderModal
        order={selectedOrder}
        isOpen={showPackOrderModal}
        onClose={() => setShowPackOrderModal(false)}
        onPackOrder={handlePackOrderSubmission}
      />

      {/* Edit Order Modal */}
      <EditOrderModal
        order={selectedOrder}
        isOpen={showEditOrderModal}
        onClose={() => setShowEditOrderModal(false)}
        onOrderUpdated={handleOrderUpdated}
      />

      {/* DHL Export Modal */}
      <DhlExportModal
        order={selectedOrder}
        isOpen={showDhlExportModal}
        onClose={() => setShowDhlExportModal(false)}
      />

      {/* Invoice Preview Modal */}
      <InvoicePreviewModal
        order={selectedOrder}
        isOpen={showInvoicePreviewModal}
        onClose={() => setShowInvoicePreviewModal(false)}
      />

      {/* Cancel Order Modal */}
      <AnimatePresence>
        {showCancelOrderModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-red-600 to-red-700 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <AlertTriangle className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Confirm Order Cancellation
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Cancel order and notify customer
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        !isCancelling && setShowCancelOrderModal(false)
                      }
                      disabled={isCancelling}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-gray-50/50 to-white">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  <div className="text-center mb-4">
                    <p className="text-gray-700 text-base leading-relaxed">
                      Are you sure you want to cancel this order? This action
                      cannot be undone and the customer will be notified.
                    </p>
                  </div>

                  {orderToCancel && (
                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        Order Details
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Order Number:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {getOrderNumber(orderToCancel)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Customer:
                          </span>
                          <span className="text-sm text-gray-900">
                            {orderToCancel.customer.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Total Amount:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatOrderCurrency(getOrderTotal(orderToCancel), orderToCancel)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Status:
                          </span>
                          <Badge
                            className={
                              getStatusConfig(orderToCancel.status).color
                            }
                          >
                            {getStatusConfig(orderToCancel.status).label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">
                          Warning: This action cannot be undone
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          The customer will be automatically notified about the
                          cancellation.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* Enhanced Footer */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-end gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowCancelOrderModal(false)}
                      disabled={isCancelling}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Keep Order
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handleConfirmCancelOrder}
                      disabled={isCancelling}
                      className="bg-red-600 hover:bg-red-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-red-600/25"
                    >
                      {isCancelling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancel Order
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Refund Order Modal */}
      <AnimatePresence>
        {showRefundOrderModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-orange-600 to-amber-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <DollarSign className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Process Order Refund
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Issue full refund to customer
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        !isRefunding && setShowRefundOrderModal(false)
                      }
                      disabled={isRefunding}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-gray-50/50 to-white">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  <div className="text-center mb-4">
                    <p className="text-gray-700 text-base leading-relaxed">
                      This will process a full refund for the cancelled order.
                      The refund may take 5-10 business days to appear on the
                      customer's statement.
                    </p>
                  </div>

                  {orderToRefund && (
                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        Refund Details
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Order Number:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {getOrderNumber(orderToRefund)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Customer:
                          </span>
                          <span className="text-sm text-gray-900">
                            {orderToRefund.customer.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Refund Amount:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatOrderCurrency(getOrderTotal(orderToRefund), orderToRefund)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">
                            Payment Status:
                          </span>
                          <Badge
                            className={
                              getPaymentStatusConfig(
                                getPaymentStatus(orderToRefund)
                              ).color
                            }
                          >
                            {
                              getPaymentStatusConfig(
                                getPaymentStatus(orderToRefund)
                              ).label
                            }
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <label
                      htmlFor="refund-reason"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Refund Reason (Optional)
                    </label>
                    <textarea
                      id="refund-reason"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Enter reason for refund..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-600/20 focus:border-orange-600 shadow-sm hover:shadow-md transition-all duration-200 resize-none"
                      rows={3}
                      disabled={isRefunding}
                    />
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-orange-50 border border-orange-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-orange-800">
                          Refund Processing Time
                        </p>
                        <p className="text-sm text-orange-700 mt-1">
                          Refunds typically take 5-10 business days to appear on
                          the customer's statement.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* Enhanced Footer */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-end gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowRefundOrderModal(false)}
                      disabled={isRefunding}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handleConfirmRefundOrder}
                      disabled={isRefunding}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-orange-600/25"
                    >
                      {isRefunding ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing Refund...
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Process Refund
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteOrder}
        loading={deleteLoading}
        title="Delete Order"
        description="This permanently removes the order and its history from JamPack'd."
        itemName={orderToDelete?.orderNumber || orderToDelete?.id}
      />

      {/* Procurement Batches Modal */}
      <ProcurementBatchesModal
        isOpen={showProcurementBatchesModal}
        onClose={() => setShowProcurementBatchesModal(false)}
        onCreateNewBatch={() => {
          setShowProcurementBatchesModal(false);
          setShowCreateBatchModal(true);
        }}
        onViewBatch={(batch) => {
          // TODO: Implement batch viewing functionality
          console.log('View batch:', batch);
        }}
      />

      {/* Create Batch Modal */}
      <CreateBatchModal
        isOpen={showCreateBatchModal}
        onClose={() => setShowCreateBatchModal(false)}
        onBatchCreated={(batchId) => {
          console.log('Batch created:', batchId);
          // Refresh orders to reflect updated inProcurement status
          refreshOrdersAfterUpdate();
        }}
      />

      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={showCreateOrderModal}
        onClose={() => setShowCreateOrderModal(false)}
        onOrderCreated={async () => {
          setShowCreateOrderModal(false);
          await refreshOrdersAfterUpdate();
        }}
      />
    </div>
  );
}
